import {
  SYMPTOM_OPTIONS,
  STANDARD_PESTS,
  SPECIALTY_PESTS,
  type PropertyType,
  type SeverityLevel,
} from "../constants";

const PEST_SEVERITY_WEIGHTS: Record<string, number> = {
  ants: 1,
  "mice-rats": 2,
  spiders: 1,
  cockroaches: 2,
  termites: 3,
  crickets: 1,
  "earwigs-silverfish": 1,
  "wasps-bees": 2,
  "bed-bugs": 3,
  "raccoons-squirrels": 2,
  snakes: 2,
  "wild-animals": 2,
};

export function getZipMultiplier(zip: string): number {
  const n = parseInt(zip, 10);
  return 0.92 + (n % 20) * 0.012;
}

export function calculateCostRange(
  sqFt: number,
  zip: string,
  propertyType: PropertyType,
): { min: number; max: number } {
  const multiplier = getZipMultiplier(zip);
  const typeFactor = propertyType === "apartment-condo" ? 0.78 : propertyType === "commercial" ? 1.35 : 1;
  const min = Math.round((80 + sqFt * 0.07) * multiplier * typeFactor);
  const max = Math.round((140 + sqFt * 0.12) * multiplier * typeFactor);
  return { min: Math.max(120, min), max: Math.max(min + 60, max) };
}

export function calculateSeverity(
  symptomIds: string[],
  pestIds: string[],
): { score: number; level: SeverityLevel; percent: number; label: string } {
  const symptomWeight = symptomIds.reduce((sum, id) => {
    const symptom = SYMPTOM_OPTIONS.find(s => s.id === id);
    return sum + (symptom?.weight ?? 0);
  }, 0);

  const pestWeight = pestIds.reduce((sum, id) => sum + (PEST_SEVERITY_WEIGHTS[id] ?? 0), 0);
  const score = symptomWeight + pestWeight;

  let level: SeverityLevel;
  let percent: number;
  let label: string;

  if (score <= 3) {
    level = "low";
    percent = 28;
    label = "Low";
  } else if (score <= 6) {
    level = "moderate";
    percent = 58;
    label = "Moderate";
  } else {
    level = "high";
    percent = 88;
    label = "High";
  }

  return { score, level, percent, label };
}

export function hasSpecialtyPest(pestIds: string[]): boolean {
  return pestIds.some(id => SPECIALTY_PESTS.some(p => p.id === id));
}

export function getIdentifiedPests(pestIds: string[]): string[] {
  return pestIds
    .map(id => ALL_PEST_LABELS[id])
    .filter((label): label is string => Boolean(label));
}

const ALL_PEST_LABELS: Record<string, string> = [...STANDARD_PESTS, ...SPECIALTY_PESTS].reduce(
  (acc, pest) => {
    acc[pest.id] = pest.label;
    return acc;
  },
  {} as Record<string, string>,
);
